import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useState } from 'react'
import {
  useAddCompanyEmailMutation,
  useAddCompanyPhoneMutation,
  useCreateCompanyMutation,
  useCreateUserCarrierAssociationMutation,
} from 'store/company/company.api'
import { getUserId } from 'utils/common'
import SubmitLockButton from 'components/submit-lock-button/submit-lock-button.component'
import { formatPhoneForSave } from 'utils/phone'
import {
  DetailField,
  detailTextFieldSx,
  primaryActionButtonSx,
} from 'pages/address/address-details/customer-details/customer-detail-fields'
import { DISPATCH_CONTACT_NAME, parseCarrierAddress } from 'pages/address/address-details/carrier-details/carrier-contact.utils'

interface AddCarrierDialogProps {
  open: boolean
  onClose: () => void
  onCreated: (associationId: string) => void
}

const AddCarrierDialog = ({ open, onClose, onCreated }: AddCarrierDialogProps) => {
  const userId = getUserId()

  const [createCompany, { isLoading: isCreatingCompany }] =
    useCreateCompanyMutation()
  const [createAssociation, { isLoading: isCreatingAssociation }] =
    useCreateUserCarrierAssociationMutation()
  const [addEmail, { isLoading: isAddingEmail }] = useAddCompanyEmailMutation()
  const [addPhone, { isLoading: isAddingPhone }] = useAddCompanyPhoneMutation()

  const [carrierName, setCarrierName] = useState('')
  const [carrierAddress, setCarrierAddress] = useState('')
  const [dispatchName, setDispatchName] = useState('')
  const [dispatchEmail, setDispatchEmail] = useState('')
  const [dispatchPhone, setDispatchPhone] = useState('')
  const [dispatchCell, setDispatchCell] = useState('')
  const [dispatchAfterHours, setDispatchAfterHours] = useState('')
  const [error, setError] = useState<string | null>(null)

  const isSaving =
    isCreatingCompany ||
    isCreatingAssociation ||
    isAddingEmail ||
    isAddingPhone

  const resetForm = () => {
    setCarrierName('')
    setCarrierAddress('')
    setDispatchName('')
    setDispatchEmail('')
    setDispatchPhone('')
    setDispatchCell('')
    setDispatchAfterHours('')
    setError(null)
  }

  const handleClose = () => {
    if (isSaving) return
    resetForm()
    onClose()
  }

  const handleSubmit = async () => {
    const trimmedName = carrierName.trim()
    if (!userId || !trimmedName) {
      setError('Carrier name is required.')
      return
    }

    setError(null)

    try {
      const company = await createCompany({
        userId,
        companyName: trimmedName,
        ...parseCarrierAddress(carrierAddress),
      }).unwrap()

      const association = await createAssociation({
        userId,
        companyId: company.id,
        dispatchName: dispatchName.trim() || undefined,
      }).unwrap()

      if (dispatchEmail.trim()) {
        await addEmail({
          companyId: company.id,
          body: {
            email: dispatchEmail.trim(),
            emailType: 'NotificationEmail',
            userType: 'company',
            contactName: DISPATCH_CONTACT_NAME,
          },
        }).unwrap()
      }

      const phoneEntries = [
        { value: dispatchPhone, phoneType: 'Dispatch' as const },
        { value: dispatchCell, phoneType: 'DispatchCell' as const },
        {
          value: dispatchAfterHours,
          phoneType: 'DispatchAfterHoursPhone' as const,
        },
      ]

      for (const entry of phoneEntries) {
        const trimmedPhone = formatPhoneForSave(entry.value.trim())
        if (!trimmedPhone) continue
        await addPhone({
          companyId: company.id,
          body: {
            phone: trimmedPhone,
            phoneType: entry.phoneType,
            userType: 'company',
            contactName: DISPATCH_CONTACT_NAME,
          },
        }).unwrap()
      }

      onCreated(association.id)
      resetForm()
      onClose()
    } catch (submitError) {
      console.error('Failed to create carrier', submitError)
      setError('Failed to create carrier. Please try again.')
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogContent sx={{ p: 3, position: 'relative' }}>
        <IconButton
          onClick={handleClose}
          disabled={isSaving}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: 'error.main',
          }}
        >
          <CloseIcon />
        </IconButton>

        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
          Add Carrier
        </Typography>

        <Box sx={{ maxWidth: 560 }}>
          <DetailField label="Carrier Name">
            <TextField
              fullWidth
              size="small"
              value={carrierName}
              onChange={(e) => setCarrierName(e.target.value)}
              disabled={isSaving}
              sx={detailTextFieldSx}
            />
          </DetailField>

          <DetailField label="Carrier Address">
            <TextField
              fullWidth
              size="small"
              value={carrierAddress}
              onChange={(e) => setCarrierAddress(e.target.value)}
              disabled={isSaving}
              sx={detailTextFieldSx}
            />
          </DetailField>

          <DetailField label="Dispatch Name">
            <TextField
              fullWidth
              size="small"
              value={dispatchName}
              onChange={(e) => setDispatchName(e.target.value)}
              disabled={isSaving}
              sx={detailTextFieldSx}
            />
          </DetailField>

          <DetailField label="Dispatch Email">
            <TextField
              fullWidth
              size="small"
              value={dispatchEmail}
              onChange={(e) => setDispatchEmail(e.target.value)}
              disabled={isSaving}
              sx={detailTextFieldSx}
            />
          </DetailField>

          <DetailField label="Dispatch Phone">
            <TextField
              fullWidth
              size="small"
              value={dispatchPhone}
              onChange={(e) => setDispatchPhone(e.target.value)}
              disabled={isSaving}
              sx={detailTextFieldSx}
            />
          </DetailField>

          <DetailField label="Dispatch Cell">
            <TextField
              fullWidth
              size="small"
              value={dispatchCell}
              onChange={(e) => setDispatchCell(e.target.value)}
              disabled={isSaving}
              sx={detailTextFieldSx}
            />
          </DetailField>

          <DetailField label="Dispatch After Hours">
            <TextField
              fullWidth
              size="small"
              value={dispatchAfterHours}
              onChange={(e) => setDispatchAfterHours(e.target.value)}
              disabled={isSaving}
              sx={detailTextFieldSx}
            />
          </DetailField>
        </Box>

        {error && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}

        <Stack direction="row" justifyContent="center" spacing={2} sx={{ mt: 3 }}>
          <SubmitLockButton
            variant="contained"
            onClick={handleSubmit}
            disabled={isSaving}
            sx={primaryActionButtonSx}
          >
            Add Carrier
          </SubmitLockButton>
        </Stack>
      </DialogContent>
    </Dialog>
  )
}

export default AddCarrierDialog
